import React, { useState } from 'react';
import { useDashboard } from '../hooks/useDashboard';
import TileEditDialog from './Tile/TileEditDialog';
import GroupEditDialog from './Group/GroupEditDialog';

interface EditManagerProps {
  editMode: boolean;
}

const EditManager: React.FC<EditManagerProps> = ({ editMode }) => {
  const { selectedItem, setSelectedItem, config, activeTab } = useDashboard();
  
  // Simple dialog states
  const [tileDialog, setTileDialog] = useState<{
    open: boolean;
    tile?: any;
    groupId?: string;
    mode: 'create' | 'edit';
  }>({
    open: false,
    mode: 'create'
  });
  
  const [groupDialog, setGroupDialog] = useState<{
    open: boolean;
    group?: any;
    mode: 'create' | 'edit';
  }>({
    open: false,
    mode: 'create'
  });

  // Handle selected item changes (from context menu)
  React.useEffect(() => {
    if (!selectedItem || !editMode) return;

    if (selectedItem.type === 'tile') {
      const tile = findTileById(selectedItem.id);
      const groupId = findGroupIdByTileId(selectedItem.id);
      
      setTileDialog({
        open: true,
        tile,
        groupId,
        mode: 'edit'
      });
    } else if (selectedItem.type === 'group') {
      const group = activeTab?.groups.find(g => g.id === selectedItem.id);
      
      setGroupDialog({
        open: true,
        group,
        mode: 'edit'
      });
    }
    
    // Clear selected item immediately
    setSelectedItem(null);
  }, [selectedItem, editMode, config, setSelectedItem]);

  // Listen for custom events to open dialogs
  React.useEffect(() => {
    const handleNewTile = (event: CustomEvent) => {
      if (!editMode) return;
      
      const groupId = event.detail?.groupId || activeTab?.groups[0]?.id;
      setTileDialog({
        open: true,
        groupId,
        mode: 'create'
      });
    };

    const handleNewGroup = () => {
      if (!editMode) return;
      
      setGroupDialog({
        open: true,
        mode: 'create'
      });
    };

    document.addEventListener('dashboard:new-tile', handleNewTile as EventListener);
    document.addEventListener('dashboard:new-group', handleNewGroup as EventListener);

    return () => {
      document.removeEventListener('dashboard:new-tile', handleNewTile as EventListener);
      document.removeEventListener('dashboard:new-group', handleNewGroup as EventListener);
    };
  }, [editMode, activeTab]);

  // Helper functions
  const findTileById = (tileId: string) => {
    if (!activeTab) return undefined;
    for (const group of activeTab.groups) {
      const tile = group.tiles.find(t => t.id === tileId);
      if (tile) return tile;
    }
    return undefined;
  };

  const findGroupIdByTileId = (tileId: string) => {
    if (!activeTab) return undefined;
    for (const group of activeTab.groups) {
      if (group.tiles.some(t => t.id === tileId)) {
        return group.id;
      }
    }
    return undefined;
  };

  // Close handlers - simple and clean
  const closeTileDialog = () => {
    setTileDialog({ open: false, mode: 'create' });
  };

  const closeGroupDialog = () => {
    setGroupDialog({ open: false, mode: 'create' });
  };

  // Only render dialogs if in edit mode
  if (!editMode) return null;

  return (
    <>
      {/* Tile Edit Dialog */}
      <TileEditDialog
        key={`tile-${tileDialog.mode}-${tileDialog.tile?.id || 'new'}-${tileDialog.open}`}
        open={tileDialog.open}
        onClose={closeTileDialog}
        tile={tileDialog.tile}
        groupId={tileDialog.groupId}
        mode={tileDialog.mode}
      />

      {/* Group Edit Dialog */}
      <GroupEditDialog
        key={`group-${groupDialog.mode}-${groupDialog.group?.id || 'new'}-${groupDialog.open}`}
        open={groupDialog.open}
        onClose={closeGroupDialog}
        group={groupDialog.group}
        mode={groupDialog.mode}
      />
    </>
  );
};

export default EditManager;